import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Brain, Activity, Heart, TrendingUp, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { CATEGORY_META, tierLabel } from "../lib/survey";

const ICONS = { mental: Brain, physical: Activity, emotional: Heart };

/**
 * Back Pain Transformation Index summary card.
 * Pulls latest BPTI for the user; shows three category bars + overall.
 * Renders nothing until the user has at least one survey.
 */
export default function BPTICard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/me/bpti").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data || data.bpti == null) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      data-testid="bpti-card"
      className="mt-6 relative overflow-hidden rounded-3xl border border-white/10 p-5"
      style={{
        background:
          "radial-gradient(120% 80% at 0% 0%, rgba(34,211,238,0.16), transparent 50%), radial-gradient(120% 80% at 100% 100%, rgba(245,158,11,0.10), transparent 55%), linear-gradient(160deg, rgba(10,21,48,0.85) 0%, rgba(3,16,31,0.95) 100%)",
        boxShadow: "0 24px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)",
      }}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="bps-eyebrow text-[#22D3EE]">Back Pain Transformation Index</div>
          <h3 className="font-display text-lg text-white mt-0.5 leading-snug">BPTI · {data.bpti}<span className="text-sm text-[#7C8FA8]">/100</span></h3>
        </div>
        <div className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.22em] px-2.5 py-1 rounded-full border" style={{ borderColor: "#22D3EE55", color: "#22D3EE" }}>
          <TrendingUp size={11} /> {tierLabel(data.bpti)}
        </div>
      </div>

      <div className="space-y-3" data-testid="bpti-categories">
        {["mental", "physical", "emotional"].map((key) => {
          const v = data[key];
          if (v == null) return null;
          const meta = CATEGORY_META[key];
          const Icon = ICONS[key];
          return (
            <div key={key} data-testid={`bpti-${key}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="inline-flex items-center gap-2 text-sm text-white">
                  <Icon size={13} strokeWidth={1.8} style={{ color: meta.color }} />
                  {meta.label}
                </span>
                <span className="font-mono text-sm font-semibold" style={{ color: meta.color }}>
                  {v}<span className="text-[10px] text-[#7C8FA8]">/100</span>
                </span>
              </div>
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${v}%` }}
                  transition={{ duration: 1.1, ease: "easeOut", delay: 0.1 }}
                  className="h-full"
                  style={{ background: meta.color, boxShadow: `0 0 10px ${meta.color}66` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-[#9AAEC6] italic mt-4 leading-relaxed flex items-start gap-2">
        <Sparkles size={11} className="mt-0.5 flex-shrink-0 text-[#22D3EE]" />
        Your lowest category is where the pain is loudest — even if it's not where you feel it.
      </p>
    </motion.div>
  );
}
